<template>
  <div class="flex flex-col gap-3">
    <form v-if="showEmailForm && !isUserLoggedIn" @submit.prevent="submitEmail">
      <p class="font-sans text-sm font-medium tracking-tight text-black">
        {{ t("pages.sell-a-profile.enter-email", { name: bundle.seller_info.user_name }) }}
      </p>

      <IAIInputText
        id="email"
        v-model="email"
        class="mt-3"
        :placeholder="t('pages.auth.enter-email.email')"
        :rules="rules.email"
        autocomplete="email"
      />
    </form>

    <div class="buttons-container">
      <slot
        name="buttons"
        :on-try-click="onTryButtonClick"
        :on-buy-click="onBuyProfileClicked"
        :is-disabled="shouldDisableButton"
      ></slot>
    </div>
  </div>

  <IAIModal v-model="isShowAuthModal" max-width="480px">
    <div class="flex justify-center">
      <img :src="ImagenLogo.src" class="w-16" alt="Imagen" />
    </div>

    <div class="card-shadow mx-auto mt-5 min-h-[100%] max-w-[356px]" :style="`min-height: ${AUTH_FLOW_HEIGHT}`">
      <Suspense>
        <AuthFlow
          :style="`min-height: ${AUTH_FLOW_HEIGHT}`"
          :user-flow="{
            name: userFlowName,
            data: { bundle_id: props.bundle.id },
          }"
          :email="email"
          :back-button="false"
          @loginSuccess="onLoginSuccess"
        />
      </Suspense>
    </div>
  </IAIModal>
</template>

<script setup lang="ts">
import { t } from "@/locales/vue";
import { ref, computed } from "vue";
import { IAIInputText, IAIUseForm, IAIModal } from "@/core-ui-vue";
import { universalStorage } from "@/utils/universal-storage";
import { funnelService } from "@/services/funnelService";
import rules from "@/features/auth/common/rules";
import AuthFlow from "@/features/auth/components/AuthFlow.vue";
import ImagenLogo from "@/features/auth/assets/imagen-logo-pink.svg";
import { IBundleDetailesResponse } from "../../services/api";
import { getProfileUserFlow, initURLWithProfileShopParam } from "@/features/profile-shop/utils";
import { USER_FLOW_PARAM, serializeUserFlow } from "@/features/auth/utils/user-flow";
const AUTH_FLOW_HEIGHT = "26rem";
const isShowAuthModal = ref(false);
const email = ref("");
const isUserLoggedIn = !!universalStorage.get("user_id");
const isMobile = universalStorage.get("mobile_or_desktop") === "mobile";

const props = defineProps<{
  bundle: IBundleDetailesResponse;
  bundleId: string;
  showEmailForm: boolean;
  userFlowName: "profile_shop" | "purchase_profile";
}>();

const { formRef, currentlyInvalid: formCurrentlyInvalid } = IAIUseForm({});

const shouldDisableButton = computed(() => props.showEmailForm && formCurrentlyInvalid.value);

const onTryButtonClick = () => {
  if (!props.showEmailForm) {
    if (!isUserLoggedIn) {
      isShowAuthModal.value = true;
      return;
    }
    onLoginSuccess();
  } else {
    submitEmail();
  }
};

const submitEmail = async () => {
  const { valid } = await formRef.validate();
  if (!valid) return;

  funnelService.trackEvent("seller_flow_next_button_clicked", {
    bundle_id: props.bundle.id,
    bundle_name: props.bundle.name,
    seller_name: props.bundle.seller_info.user_name,
    seller_source: props.bundle.seller_info.source,
    user_flow: getProfileUserFlow(props.userFlowName === "profile_shop"),
    profile_tags: props.bundle.bundle_tags?.map((tag) => tag.label),
  });

  if (isUserLoggedIn) {
    onLoginSuccess();
  } else {
    isShowAuthModal.value = true;
  }
};

const onBuyProfileClicked = () => {
  funnelService.trackEvent("seller_flow_direct_purchase_clicked", {
    bundle_id: props.bundle.id,
    bundle_name: props.bundle.name,
    seller_name: props.bundle.seller_info.user_name,
    seller_source: props.bundle.seller_info.source,
    user_flow: getProfileUserFlow(props.userFlowName === "profile_shop"),
    profile_tags: props.bundle.bundle_tags?.map((tag) => tag.label),
  });

  const url = initURLWithProfileShopParam(
    `${window.location.origin}/bundle/purchase/${props.bundleId}`,
    props.userFlowName === "profile_shop"
  );
  url.searchParams.append(
    USER_FLOW_PARAM,
    serializeUserFlow({
      name: props.userFlowName,
      data: {
        bundle_id: props.bundle.id,
      },
    })
  );

  window.location.assign(url.toString());
};

function onLoginSuccess() {
  if (isMobile) {
    const url = initURLWithProfileShopParam(
      `${window.location.origin}/bundle/switch-to-desktop`,
      props.userFlowName === "profile_shop"
    );
    url.searchParams.append("bundle", props.bundleId);
    url.searchParams.append("send-email", "true");
    window.location.assign(url.toString());
  } else {
    const url = initURLWithProfileShopParam(
      `${window.location.origin}/bundle/${props.bundle.should_show_preview ? "preview" : "purchase"}/${props.bundleId}`,
      props.userFlowName === "profile_shop"
    );
    window.location.assign(url.toString());
  }
}
</script>

<style scoped lang="scss">
.buttons-container {
  display: flex;
  gap: 1rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
